// ============================================================================
// Browser-side brand compositor (PRD §6.3, §6.4).
//
// Replaces the server-side CanvasKit/Skia compositor for the free Canvas path:
// the heavy WASM engine could not run inside the Supabase Edge isolate (it blew
// the CPU limit and the job was hard-killed). The browser already shapes complex
// Arabic text natively (HarfBuzz) on a 2D <canvas>, so we composite here instead
// — no WASM, no CPU caps, no font fetch on the server, no credits.
//
// Layout proportions are kept identical to the old server compositor so output
// matches the original design intent. Logo & phone are drawn here (never AI).
// ============================================================================

// Stable static fonts from the @fontsource CDN (jsDelivr). Loaded via FontFace
// so the canvas can use them; falls back to system fonts if the CDN is down.
const FONT_FACES: Array<{ family: string; weight: string; url: string }> = [
  {
    family: "Cairo",
    weight: "700",
    url: "https://cdn.jsdelivr.net/npm/@fontsource/cairo/files/cairo-arabic-700-normal.woff2",
  },
  {
    family: "Inter",
    weight: "700",
    url: "https://cdn.jsdelivr.net/npm/@fontsource/inter/files/inter-latin-700-normal.woff2",
  },
  {
    family: "Inter",
    weight: "600",
    url: "https://cdn.jsdelivr.net/npm/@fontsource/inter/files/inter-latin-600-normal.woff2",
  },
];

let _fontsPromise: Promise<void> | null = null;

/** Loads the brand fonts once. Never throws — a CDN miss just falls back. */
function ensureFonts(): Promise<void> {
  if (_fontsPromise) return _fontsPromise;
  _fontsPromise = (async () => {
    try {
      await Promise.all(
        FONT_FACES.map(async (f) => {
          const face = new FontFace(f.family, `url(${f.url})`, { weight: f.weight });
          await face.load();
          document.fonts.add(face);
        }),
      );
      await document.fonts.ready;
    } catch {
      /* fall back to system fonts */
    }
  })();
  return _fontsPromise;
}

async function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // avoid tainting the canvas (public buckets send CORS)
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export interface CanvasDesignInput {
  width: number;
  height: number;
  headline: string;
  language: "ar" | "en";
  /** Brand color for the phone pill. */
  brandColorA: string;
  brandColorB: string;
  /** Optional AI/stock background; subtle white→grey gradient is used when absent. */
  backgroundUrl?: string | null;
  logoUrl?: string | null;
  phone?: string | null;
  /**
   * Uploaded doctor photo (Canvas path) — drawn as a square crop in the bottom
   * corner. `null`/absent draws no doctor — used by the AI path, where the
   * doctor is already in the background image.
   */
  doctorPhotoUrl?: string | null;
  /**
   * Draw the centered headline + logo + phone. Defaults to true (Canvas path).
   * The AI path sets this false: the AI image already contains the headline, so
   * only the logo + phone are overlaid.
   */
  drawHeadline?: boolean;
}

/** Draws the brand layer and returns final PNG bytes as a Blob. */
export async function composeCanvasDesign(input: CanvasDesignInput): Promise<Blob> {
  await ensureFonts();
  const { width: W, height: H } = input;
  const isAr = input.language === "ar";

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  const drawHeadline = input.drawHeadline !== false;

  // 1) Background — AI/stock cover-fit if provided, else subtle white→grey.
  const bg = input.backgroundUrl ? await loadImage(input.backgroundUrl) : null;
  if (bg) {
    drawCover(ctx, bg, 0, 0, W, H);
  } else {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#ffffff");
    g.addColorStop(1, "#e5e7eb");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  // 2) Optional doctor image (Canvas path only) — square crop of the uploaded
  //    photo with rounded corners, bottom corner on the headline side.
  if (input.doctorPhotoUrl) {
    const photo = await loadImage(input.doctorPhotoUrl);
    if (photo) {
      const size = Math.round(W * 0.34);
      const x = isAr ? Math.round(W * 0.06) : W - size - Math.round(W * 0.06);
      const y = H - size - Math.round(H * 0.06);
      const r = Math.round(size * 0.08);
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x, y, size, size, r);
      ctx.closePath();
      ctx.clip();
      drawCover(ctx, photo, x, y, size, size);
      ctx.restore();
    }
  }

  // 3) Headline — large and centered (both axes), with a soft drop shadow for
  //    legibility. Native shaping keeps Arabic RTL correct even when centered.
  if (drawHeadline) {
    const fontSize = Math.round(W * 0.09);
    const pad = Math.round(W * 0.06);
    const maxWidth = W - pad * 2;

    ctx.save();
    ctx.font = isAr
      ? `700 ${fontSize}px Cairo, Inter, system-ui, sans-serif`
      : `700 ${fontSize}px Inter, Cairo, system-ui, sans-serif`;
    ctx.textBaseline = "top";
    ctx.direction = isAr ? "rtl" : "ltr";

    const lines = wrapText(ctx, input.headline, maxWidth);
    const lineHeight = Math.round(fontSize * 1.25);
    const blockH = lines.length * lineHeight;
    const startY = Math.round((H - blockH) / 2);

    // Soft drop shadow behind the text (no scrim box) for legibility.
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.shadowColor = "rgba(0,0,0,0.45)";
    ctx.shadowBlur = Math.round(fontSize * 0.22);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.round(fontSize * 0.05);
    let y = startY;
    for (const line of lines) {
      ctx.fillText(line, W / 2, y);
      y += lineHeight;
    }
    ctx.restore();
  }

  // 4) Logo — composited from the profile asset, always top-left.
  if (input.logoUrl) {
    const logo = await loadImage(input.logoUrl);
    if (logo) {
      const lw = Math.round(W * 0.18);
      const lh = Math.round((logo.height / logo.width) * lw);
      const x = Math.round(W * 0.05);
      const y = Math.round(H * 0.05);
      ctx.drawImage(logo, x, y, lw, lh);
    }
  }

  // 5) Phone — crisp text in a brand-colored pill at the bottom.
  if (input.phone) {
    const pw = Math.round(W * 0.5);
    const ph = Math.round(H * 0.06);
    const px = Math.round((W - pw) / 2);
    const py = H - ph - Math.round(H * 0.03);

    ctx.save();
    ctx.fillStyle = input.brandColorA;
    ctx.beginPath();
    ctx.roundRect(px, py, pw, ph, ph / 2);
    ctx.fill();

    ctx.font = `600 ${Math.round(W * 0.038)}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = "#ffffff";
    ctx.direction = "ltr";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(input.phone, W / 2, py + ph / 2);
    ctx.restore();
  }

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Failed to encode PNG"))),
      "image/png",
    ),
  );
}

/** Cover-fit an image into a destination rect (center-cropped). */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
) {
  const scale = Math.max(dw / img.width, dh / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, dx + (dw - w) / 2, dy + (dh - h) / 2, w, h);
}

/** Greedy word-wrap to a max pixel width (caps at 3 lines, ellipsis on overflow). */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const trial = current ? `${current} ${word}` : word;
    if (ctx.measureText(trial).width <= maxWidth || !current) {
      current = trial;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (lines.length > 3) {
    lines.length = 3;
    lines[2] = lines[2].replace(/\s+\S*$/, "") + "…";
  }
  return lines;
}
